// ============================================================================
// Single task row. Used by both the global /tasks list and the per-facility
// embedded pane. Click → opens detail; quick actions sit in the kebab menu.
// ============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Clock,
  RotateCcw,
  MoreVertical,
  Pencil,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../../shared/components';
import type {
  TaskCategory,
  TaskPriority,
  TaskSource,
  TaskStatus,
} from '../../shared/types';
import type { FacilityTaskRow } from './hooks/use-task-data';

interface Props {
  task: FacilityTaskRow;
  showFacility?: boolean;
  onComplete?: (taskId: string) => void | Promise<void>;
  onDefer?: (taskId: string) => void | Promise<void>;
  onReopen?: (taskId: string) => void | Promise<void>;
  onEdit?: (taskId: string) => void;
  // AFB-0012: promotes a primary inline "Resolve" action out of the kebab on
  // the signed-in user's own open task. currentEid is the viewer's employee_id.
  onResolve?: (taskId: string) => void;
  currentEid?: string | null;
  busy?: boolean;
}

const PRIORITY_STYLE: Record<TaskPriority, string> = {
  High: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  Medium: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Low: 'bg-tcs-surface text-tcs-text-secondary',
};

const STATUS_STYLE: Record<TaskStatus, string> = {
  Open: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  In_Progress: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Done: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Deferred: 'bg-tcs-surface text-tcs-text-secondary',
};

const SOURCE_LABEL: Record<TaskSource, string> = {
  Manual: 'Manual',
  Feedback_Auto: 'From feedback',
  Inspection_Auto: 'From inspection',
  Supply_Auto: 'From supply',
  Sub_Auto: 'From sub setup',
  AM_Copilot: 'From Ask AI',
};

// Color-coded category badge — sits between Priority and Status on each card
// so role-based filtering is visible at a glance.
const CATEGORY_STYLE: Record<TaskCategory, string> = {
  Operations: 'bg-tcs-surface text-tcs-text-secondary',
  Finance: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  HR: 'bg-violet-100 text-violet-800',
  Sub_Management: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Sales: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Other: 'bg-tcs-surface text-tcs-text-secondary',
};

function dueState(due: string | null): {
  label: string;
  cls: string;
} | null {
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(due + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const ms = d.getTime() - today.getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const fmt = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  if (days < 0) return { label: `Overdue · ${fmt}`, cls: 'text-st-danger-fg' };
  if (days <= 2) return { label: `Due ${fmt}`, cls: 'text-st-warn-fg' };
  return { label: `Due ${fmt}`, cls: 'text-tcs-text-muted' };
}

function assigneeLabel(t: FacilityTaskRow): string | null {
  const f = (t.assignee_first ?? '').trim();
  const l = (t.assignee_last ?? '').trim();
  const both = `${f} ${l}`.trim();
  return both.length > 0 ? both : null;
}

export default function TaskCard({
  task,
  showFacility = false,
  onComplete,
  onDefer,
  onReopen,
  onEdit,
  onResolve,
  currentEid,
  busy = false,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const due = dueState(task.due_date);
  const assignee = assigneeLabel(task);
  const isClosed = task.status === 'Done' || task.status === 'Deferred';
  const commentCount = task.comments?.length ?? 0;
  // Primary inline "Resolve" only on the viewer's own still-open task.
  const showResolveInline =
    !isClosed &&
    Boolean(onResolve) &&
    Boolean(currentEid) &&
    task.assigned_to === currentEid;

  return (
    <li className="rounded-md border border-tcs-border bg-tcs-raised p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-tcs-text-muted">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${PRIORITY_STYLE[task.priority]}`}
          >
            {task.priority}
          </span>
          {task.category && (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${CATEGORY_STYLE[task.category]}`}
            >
              {task.category.replace('_', ' ')}
            </span>
          )}
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_STYLE[task.status]}`}
          >
            {task.status.replace('_', ' ')}
          </span>
          {due && <span className={`text-xs ${due.cls}`}>{due.label}</span>}
          {task.source !== 'Manual' && (
            <span className="text-xs text-tcs-text-muted">
              · {SOURCE_LABEL[task.source]}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showResolveInline && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onResolve?.(task.task_id)}
              disabled={busy}
            >
              <Check size={13} className="mr-1" /> Resolve
            </Button>
          )}
          <Link
            to={`/tasks/${task.task_id}`}
            className="text-[10px] uppercase tracking-wide text-tcs-text-muted hover:text-tcs-text-secondary"
          >
            {task.task_id}
          </Link>
          <div className="relative">
            <button
              type="button"
              aria-label="More actions"
              className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text-secondary"
              onClick={() => setMenuOpen((o) => !o)}
              disabled={busy}
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-tcs-border bg-tcs-overlay py-1 shadow-md"
                onMouseLeave={() => setMenuOpen(false)}
              >
                {!isClosed && onComplete && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-tcs-text-secondary hover:bg-tcs-surface"
                    onClick={() => {
                      setMenuOpen(false);
                      void onComplete(task.task_id);
                    }}
                  >
                    <Check size={14} /> Mark complete
                  </button>
                )}
                {!isClosed && onDefer && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-tcs-text-secondary hover:bg-tcs-surface"
                    onClick={() => {
                      setMenuOpen(false);
                      void onDefer(task.task_id);
                    }}
                  >
                    <Clock size={14} /> Defer
                  </button>
                )}
                {isClosed && onReopen && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-tcs-text-secondary hover:bg-tcs-surface"
                    onClick={() => {
                      setMenuOpen(false);
                      void onReopen(task.task_id);
                    }}
                  >
                    <RotateCcw size={14} /> Reopen
                  </button>
                )}
                {onEdit && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-tcs-text-secondary hover:bg-tcs-surface"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit(task.task_id);
                    }}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="mt-2 text-sm text-tcs-text">{task.description}</p>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-tcs-text-muted">
        {showFacility && task.facility_id && (
          <Link
            to={`/account-management/facilities/${task.facility_id}`}
            className="hover:text-tcs-text hover:underline"
          >
            {task.facility_name ?? task.facility_id}
          </Link>
        )}
        {assignee && <span>· Assigned to {assignee}</span>}
        {commentCount > 0 && (
          <span className="inline-flex items-center gap-1 text-tcs-text-muted">
            <MessageSquare size={12} /> {commentCount}
          </span>
        )}
      </div>

      {task.completion_notes && (
        <p className="mt-2 rounded bg-tcs-bg-muted p-2 text-xs text-tcs-text-secondary">
          {task.completion_notes}
        </p>
      )}
    </li>
  );
}
